import { useEffect, useRef } from "react";
import { createCalendarStream } from "../lib/sse";
import { useEventStore } from "../stores/eventStore";
import type { ApiCalendarEntry } from "../lib/types";

export function useServerSync(visibleCalendarIds: string[]) {
  const mergeEvent = useEventStore((s) => s.mergeEvent);
  const removeEvent = useEventStore((s) => s.removeEvent);
  const cleanupRef = useRef<Map<string, () => void>>(new Map());

  useEffect(() => {
    const currentStreams = cleanupRef.current;
    const desiredIds = new Set(visibleCalendarIds);

    // Close streams for calendars no longer visible
    for (const [id, cleanup] of currentStreams) {
      if (!desiredIds.has(id)) {
        cleanup();
        currentStreams.delete(id);
      }
    }

    // Open streams for newly visible calendars
    for (const id of desiredIds) {
      if (!currentStreams.has(id)) {
        const cleanup = createCalendarStream(id, (type, _calendarId, event) => {
          if (type === "created" || type === "updated") {
            mergeEvent(event as ApiCalendarEntry);
          } else if (type === "deleted") {
            removeEvent((event as { id: string }).id);
          }
        });
        currentStreams.set(id, cleanup);
      }
    }

    return () => {
      for (const cleanup of currentStreams.values()) {
        cleanup();
      }
      currentStreams.clear();
    };
  }, [visibleCalendarIds.join(","), mergeEvent, removeEvent]);
}
